// src/components/Header/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './index.css';

const Header = ({ toggleTheme, theme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [siteConfig, setSiteConfig] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  
  const { user, isAuthenticated, logout, loading } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // 添加调试信息
  console.log('Header渲染 - 认证状态:', { 
    isAuthenticated, 
    user, 
    loading,
    localStorageUser: localStorage.getItem('cat_blog_current_user')
  });

  // 监听滚动
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 获取网站配置
  useEffect(() => {
    const mockSiteConfig = {
      title: '喵星人日记',
      subtitle: '记录与猫咪共度的美好时光'
    };
    setSiteConfig(mockSiteConfig);
  }, []);

  // 监听用户状态变化，强制重新渲染
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 处理登录/登出
  const handleAuth = async () => {
    if (isAuthenticated && user) {
      // 调用全局的登出函数
      await logout();
      setUserMenuOpen(false);
      navigate('/');
      window.location.reload(); // 强制刷新页面以确保状态更新
    } else {
      setUserMenuOpen(false);
      navigate('/auth');
    }
  };

  // 点击个人中心
  const handleUserCenter = () => {
    if (isAuthenticated && user) {
      navigate('/user');
      setUserMenuOpen(false);
      setIsMenuOpen(false);
    } else {
      navigate('/auth');
    }
  };

  // 点击用户头像
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      {/* 顶部装饰条 */}
      <div className="header-top-decoration">
        <div className="cat-paw-line">
          {[...Array(10)].map((_, i) => (
            <span key={i} className="paw-icon">🐾</span>
          ))}
        </div>
      </div>

      {/* 主要内容 */}
      <div className="header-container">
        {/* Logo区域 */}
        <div className="logo-area">
          <Link to="/" className="logo-link">
            <div className="logo-icon">
              <div className="cat-face">
                <div className="cat-ear left"></div>
                <div className="cat-ear right"></div>
                <div className="cat-eye left"></div>
                <div className="cat-eye right"></div>
                <div className="cat-nose"></div>
                <div className="cat-mouth"></div>
                <div className="cat-whisker left-whisker-1"></div>
                <div className="cat-whisker left-whisker-2"></div>
                <div className="cat-whisker right-whisker-1"></div>
                <div className="cat-whisker right-whisker-2"></div>
              </div>
            </div>
            <div className="logo-text">
              <h1 className="site-title">{siteConfig?.title || '喵星人日记'}</h1>
              <p className="site-subtitle">{siteConfig?.subtitle || '记录与猫咪共度的美好时光'}</p>
            </div>
          </Link>
        </div>

        {/* 导航菜单 */}
        <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
          <ul className="nav-list">
            <li className="nav-item">
              <NavLink 
                to="/" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                end
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="nav-icon">🏠</span>
                <span className="nav-text">首页</span>
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/archive" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="nav-icon">📚</span>
                <span className="nav-text">归档</span>
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/about" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="nav-icon">🐱</span>
                <span className="nav-text">关于</span>
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/write" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="nav-icon">✏️</span>
                <span className="nav-text">写文章</span>
              </NavLink>
            </li>
            {/* 只有登录后才显示个人中心导航项 */}
            {isAuthenticated && (
              <li className="nav-item user-nav-item">
                <NavLink 
                  to="/user" 
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="nav-icon">👤</span>
                  <span className="nav-text">个人中心</span>
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* 用户区域 */}
        <div className="user-area">
          {isAuthenticated && user ? (
            <div className="user-profile-wrapper" ref={userMenuRef}>
              <button 
                className={`user-profile-btn ${userMenuOpen ? 'active' : ''}`}
                onClick={toggleUserMenu}
              >
                <div className="user-avatar">
                  <img 
                    src={user.avatar} 
                    alt={user.name}
                    className="avatar-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=40&h=40&fit=crop&crop=face';
                    }}
                  />
                  <div className="online-dot"></div>
                </div>
                <span className="user-name">{user.name}</span>
                <span className="user-menu-arrow">▼</span>
              </button>
              
              {/* 用户下拉菜单 */}
              <div className={`user-dropdown-menu ${userMenuOpen ? 'open' : ''}`}>
                <div className="user-dropdown-header">
                  <img 
                    src={user.avatar} 
                    alt={user.name}
                    className="dropdown-avatar"
                  />
                  <div className="dropdown-user-info">
                    <strong>{user.name}</strong>
                    <small>{user.bio || '爱猫人士'}</small>
                    <div className="user-email">{user.email || '未设置邮箱'}</div>
                  </div>
                </div>
                
                <div className="dropdown-divider"></div>
                
                <button 
                  className="dropdown-item"
                  onClick={handleUserCenter}
                >
                  <span className="dropdown-icon">👤</span>
                  <span className="dropdown-text">个人中心</span>
                </button>
                
                <div className="dropdown-divider"></div>
                
                <button 
                  className="dropdown-item logout"
                  onClick={handleAuth}
                >
                  <span className="dropdown-icon">🚪</span>
                  <span className="dropdown-text">退出登录</span>
                </button>
              </div>
            </div>
          ) : (
            <button onClick={handleAuth} className="login-button">
              <span className="login-icon">🔑</span>
              <span className="login-text">登录/注册</span>
            </button>
          )}
        </div>

        {/* 移动端菜单按钮 */}
        <button 
          className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="菜单"
        >
          <span className="menu-line top"></span>
          <span className="menu-line middle"></span>
          <span className="menu-line bottom"></span>
        </button>
      </div>

      {/* 浮动猫咪装饰 */}
      <div className="floating-cat-decoration">
        <div className="floating-cat cat-1">😺</div>
        <div className="floating-cat cat-2">😸</div>
        <div className="floating-cat cat-3">😻</div>
      </div>
    </header>
  );
};

export default Header;